// src/pages/ModuleDetail.jsx
import React, { useEffect, useState } from "react";
import { db } from "./firebase/firebaseConfig";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";

const Wrapper = styled.div`
  padding: 40px;
  color: #fff;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 10px;
`;

const Button = styled.button`
  background: linear-gradient(135deg, #00d4ff, #00bcd4);
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  color: #121212;
  font-weight: bold;
  margin-bottom: 20px;
  cursor: pointer;
`;

const ActionButton = styled.button`
  margin-top: 10px;
  margin-right: 8px;
  background: ${(props) => props.bg || "#00d4ff"};
  border: none;
  padding: 6px 10px;
  border-radius: 6px;
  color: #121212;
  cursor: pointer;
`;

const DeleteButton = styled(ActionButton)`
  background: #ff4d4f;
  color: #fff;
`;

const ModuleDetail = () => {
  const { prepId, moduleId } = useParams();
  const navigate = useNavigate();

  const [module, setModule] = useState(null);
  const [topics, setTopics] = useState([]);

  // edit state
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [editTitle, setEditTitle] = useState("");

  // ✅ Fetch module
  const fetchModule = async () => {
    try {
      const snap = await getDoc(
        doc(db, `interviewPreps/${prepId}/modules`, moduleId)
      );
      if (snap.exists()) {
        setModule({ id: snap.id, ...snap.data() });
      }
    } catch (err) {
      console.error(err);
    }
  };

  // ✅ Fetch topics
  const fetchTopics = async () => {
    try {
      const q = query(
        collection(db, `interviewPreps/${prepId}/modules/${moduleId}/topics`),
        orderBy("createdAt", "desc")
      );

      const snap = await getDocs(q);
      setTopics(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    }
  };

  // ✅ Delete topic
  const handleDelete = async (topicId) => {
    const confirmDelete = window.confirm("Delete this topic?");
    if (!confirmDelete) return;

    try {
      await deleteDoc(
        doc(db, `interviewPreps/${prepId}/modules/${moduleId}/topics`, topicId)
      );

      setTopics((prev) => prev.filter((t) => t.id !== topicId));
    } catch (err) {
      console.error(err);
    }
  };

  // ✅ Update topic
  const handleUpdate = async (topicId) => {
    if (!editTitle.trim()) return;

    try {
      await updateDoc(
        doc(db, `interviewPreps/${prepId}/modules/${moduleId}/topics`, topicId),
        {
          title: editTitle,
        }
      );

      setTopics((prev) =>
        prev.map((t) => (t.id === topicId ? { ...t, title: editTitle } : t))
      );

      setEditingTopicId(null);
      setEditTitle("");
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchModule();
    fetchTopics();
  }, []);

  return (
    <Wrapper>
      <h2>{module?.title || "Module Detail"}</h2>

      {/* ➕ Add Topic */}
      <Button onClick={() => navigate(`/add-topic/${prepId}/${moduleId}`)}>
        ➕ Add Topic
      </Button>

      <Grid>
        {topics.map((topic) => (
          <Card key={topic.id}>
            {editingTopicId === topic.id ? (
              <>
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px",
                    borderRadius: "6px",
                    marginBottom: "10px",
                  }}
                />

                <ActionButton onClick={() => handleUpdate(topic.id)}>
                  💾 Save
                </ActionButton>
                <ActionButton bg="#ccc" onClick={() => setEditingTopicId(null)}>
                  ❌ Cancel
                </ActionButton>
              </>
            ) : (
              <>
                <h3>{topic.title}</h3>

                <small style={{ color: "#aaa" }}>
                  {topic.createdAt?.toDate().toLocaleString()}
                </small>

                {/* View */}
                <ActionButton
                  onClick={() =>
                    navigate(`/topic-detail/${prepId}/${moduleId}/${topic.id}`)
                  }
                >
                  👁 View
                </ActionButton>

                {/* Edit */}
                <ActionButton
                  bg="#ffaa00"
                  onClick={() => {
                    setEditingTopicId(topic.id);
                    setEditTitle(topic.title);
                  }}
                >
                  ✏️ Edit
                </ActionButton>

                {/* Delete */}
                <DeleteButton onClick={() => handleDelete(topic.id)}>
                  🗑 Delete
                </DeleteButton>
              </>
            )}
          </Card>
        ))}
      </Grid>
    </Wrapper>
  );
};

export default ModuleDetail;
